// -----------------------------------------------------------
// FloraIQ - Metodologia FloraIQ-LADM (Página Principal)
// -----------------------------------------------------------

import { useState, useCallback } from "react";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import { ladmKpiStats } from "@/mocks/ladm";
import LadmOverview from "./components/LadmOverview";
import LadmConceptMatrix from "./components/LadmConceptMatrix";
import OperationalFlowStepper from "./components/OperationalFlowStepper";
import TerritorialLinksSection from "./components/TerritorialLinksSection";
import SourcesTraceabilitySection from "./components/SourcesTraceabilitySection";
import RrrMethodologicalSection from "./components/RrrMethodologicalSection";
import HistoryVersioningSection from "./components/HistoryVersioningSection";
import MethodsQualitySection from "./components/MethodsQualitySection";
import MethodsReportsGrid from "./components/MethodsReportsGrid";
import TreeMethodologyDetailDrawer from "./components/TreeMethodologyDetailDrawer";

type TabId = "overview" | "matrix" | "flow" | "territorial" | "sources" | "rrr" | "history" | "quality" | "reports";

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", label: "Visão Geral", icon: "ri-eye-line" },
  { id: "matrix", label: "Matriz LADM-FloraIQ", icon: "ri-table-line" },
  { id: "flow", label: "Fluxo Operacional", icon: "ri-git-branch-line" },
  { id: "territorial", label: "Vínculos Territoriais", icon: "ri-map-pin-line" },
  { id: "sources", label: "Fontes e Evidências", icon: "ri-folder-info-line" },
  { id: "rrr", label: "RRR Metodológico", icon: "ri-shield-check-line" },
  { id: "history", label: "Histórico", icon: "ri-history-line" },
  { id: "quality", label: "Qualidade", icon: "ri-check-double-line" },
  { id: "reports", label: "Relatórios", icon: "ri-file-chart-line" },
];

export default function LadmMethodologyPage() {
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [validating, setValidating] = useState(false);

  const handleValidate = useCallback(() => {
    setValidating(true);
    setActiveTab("quality");
    setTimeout(() => setValidating(false), 600);
  }, []);

  const handleGoToFlow = useCallback(() => {
    setActiveTab("flow");
  }, []);

  const renderTab = () => {
    switch (activeTab) {
      case "overview": return <LadmOverview />;
      case "matrix": return <LadmConceptMatrix />;
      case "flow": return <OperationalFlowStepper />;
      case "territorial": return <TerritorialLinksSection />;
      case "sources": return <SourcesTraceabilitySection />;
      case "rrr": return <RrrMethodologicalSection />;
      case "history": return <HistoryVersioningSection />;
      case "quality": return <MethodsQualitySection autoValidate={validating} />;
      case "reports": return <MethodsReportsGrid />;
      default: return <LadmOverview />;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Metodologia FloraIQ-LADM"
        subtitle="Organize o cadastramento arbóreo como uma base territorial inteligente, relacionando árvores, unidades espaciais, fontes, responsáveis, restrições, eventos e histórico."
      >
        <button
          onClick={handleGoToFlow}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-git-branch-line mr-1"></i>
          <span className="hidden sm:inline">Ver Fluxo Metodológico</span>
        </button>
        <button
          onClick={handleValidate}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-check-double-line mr-1"></i>
          <span className="hidden sm:inline">Validar Projeto</span>
        </button>
        <button
          onClick={() => setActiveTab("reports")}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-file-chart-line mr-1"></i>
          <span className="hidden sm:inline">Gerar Relatório</span>
        </button>
        <button
          onClick={() => setActiveTab("matrix")}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-file-excel-2-line mr-1"></i>
          <span className="hidden sm:inline">Exportar Matriz LADM</span>
        </button>
        <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
          <i className="ri-settings-3-line mr-1"></i>
          <span className="hidden sm:inline">Configurar Regras</span>
        </button>
        <button
          onClick={() => setDrawerOpen(true)}
          className="btn-primary text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-eye-line mr-1"></i>
          <span className="hidden sm:inline">Detalhe da Árvore</span>
        </button>
      </PageHeader>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard
          label="Árvores com Unidade Esp."
          value={ladmKpiStats.arvoresComUnidadeEspacial.toLocaleString("pt-BR")}
          icon="ri-map-pin-line"
          variant="success"
          trend={{ value: "86,8%", positive: true }}
        />
        <KpiCard
          label="Árvores com Fonte Documental"
          value={ladmKpiStats.arvoresComFonteDocumental.toLocaleString("pt-BR")}
          icon="ri-folder-info-line"
          variant="info"
          trend={{ value: "88,9%", positive: true }}
        />
        <KpiCard
          label="Árvores com Responsável"
          value={ladmKpiStats.arvoresComResponsavelVinculado.toLocaleString("pt-BR")}
          icon="ri-user-3-line"
          variant="info"
          trend={{ value: "86,9%", positive: true }}
        />
        <KpiCard
          label="RRR Registrados"
          value={ladmKpiStats.rrrRegistrados}
          icon="ri-shield-check-line"
          variant="default"
        />
        <KpiCard
          label="Eventos Versionados"
          value={ladmKpiStats.eventosVersionados.toLocaleString("pt-BR")}
          icon="ri-history-line"
          variant="success"
        />
        <KpiCard
          label="Fontes Técnicas"
          value={ladmKpiStats.fontesTecnicasCadastradas.toLocaleString("pt-BR")}
          icon="ri-database-2-line"
          variant="info"
        />
        <KpiCard
          label="Nuvens de Pontos"
          value={ladmKpiStats.nuvensDePontosVinculadas}
          icon="ri-stack-line"
          variant="info"
        />
        <KpiCard
          label="Qualidade Metodológica"
          value={`${ladmKpiStats.qualidadeMetodologicaMedia}%`}
          icon="ri-check-double-line"
          variant={ladmKpiStats.qualidadeMetodologicaMedia >= 80 ? "success" : "warning"}
        />
      </div>

      {/* Aviso conceitual */}
      <div className="bg-blue-50/60 border border-blue-200/60 rounded-card p-4 text-sm text-foreground-600">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-blue-600"></i>
          </div>
          <p>
            A metodologia FloraIQ-LADM estrutura o inventário arbóreo como uma base georreferenciada, auditável e integrada à gestão territorial, 
            permitindo rastrear quem informou, quando informou, qual fonte comprova e qual responsabilidade está vinculada.
          </p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="border-b border-background-200/50 overflow-x-auto scrollbar-thin">
          <div className="flex px-1 pt-1 gap-0.5 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer rounded-t-md border-b-2 -mb-[1px] ${
                  activeTab === tab.id
                    ? "text-blue-600 border-blue-500 bg-blue-50/50"
                    : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50/50"
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 fade-in" key={activeTab}>
          {renderTab()}
        </div>
      </div>

      {/* Drawer */}
      <TreeMethodologyDetailDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}